import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import API_URL from '../api';
import QuizEngine from './QuizEngine';
import './QuizBuilder.css';
import { getInitialStudentInfo, checkAutoRegister } from '../utils/queryPrefill';

const QuizPlay = () => {
    const { hash } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const completionStorageKey = `quiz-link-completed:${hash}`;
    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isRegistered, setIsRegistered] = useState(false);
    const [studentInfo, setStudentInfo] = useState(() =>
        getInitialStudentInfo(location.search)
    );
    const [result, setResult] = useState(null);
    const [pendingResult, setPendingResult] = useState(null);
    const [submitError, setSubmitError] = useState('');
    const [submittingResult, setSubmittingResult] = useState(false);
    const [availableCities, setAvailableCities] = useState([]);
    const [startTime] = useState(Date.now());

    useEffect(() => {
        const fetchCities = async () => {
            try {
                const res = await axios.get(`${API_URL}/cities`);
                setAvailableCities(res.data);
            } catch (err) {
                console.error('fetchCities:', err);
            }
        };
        fetchCities();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API_URL}/quiz/hash/${hash}${location.search}`);
                if (res.data.usageMode === 'single' && localStorage.getItem(completionStorageKey) === '1' && !new URLSearchParams(location.search).get('assigned')) {
                    navigate('/inactive');
                    return;
                }
                setQuiz(res.data);
                
                const cityBindingEnabled = res.data.city && res.data.cityBindingEnabled;
                const defaults = cityBindingEnabled ? { city: res.data.city } : {};
                const info = getInitialStudentInfo(location.search, defaults);
                
                setStudentInfo(info);
                if (checkAutoRegister(info, cityBindingEnabled)) {
                    setIsRegistered(true);
                }
            } catch (err) {
                console.error('fetchQuiz:', err);
                if (err.response?.status === 410) navigate('/inactive');
            } finally {
                setLoading(false);
            }
        })();
    }, [completionStorageKey, hash, navigate, location.search]);

    const handleRegister = (event) => {
        event.preventDefault();
        if (studentInfo.firstName && studentInfo.lastName && studentInfo.position) {
            setIsRegistered(true);
        }
    };

    const checkAnswer = async (questionIndex, answerIndex, answerIndices) => {
        const res = await axios.post(`${API_URL}/quiz/check-answer`, { hash, questionIndex, answerIndex, answerIndices });
        return res.data;
    };

    const submitQuizResult = async (rawAnswers = pendingResult?.rawAnswers || []) => {
        setSubmittingResult(true);
        setSubmitError('');

        try {
            const isAssigned = new URLSearchParams(location.search).get('assigned') === '1';
            const res = await axios.post(`${API_URL}/quiz/hash/${hash}/submit`, {
                studentName: studentInfo.firstName,
                studentLastName: studentInfo.lastName,
                studentCity: studentInfo.city,
                studentPosition: studentInfo.position,
                externalId: studentInfo.externalId,
                answers: rawAnswers,
                webhookUrl: sessionStorage.getItem('serviq_webhook_url') || '',
                timeSpent: Math.round((Date.now() - startTime) / 1000),
                allowRetake: isAssigned
            });
            localStorage.setItem(completionStorageKey, '1');
            setResult(res.data);
            setSubmitError('');
        } catch (err) {
            console.error('submit error:', err);
            setSubmitError(err.response?.data?.error || 'Результат не вдалося зберегти. Спробуйте ще раз.');
        } finally {
            setSubmittingResult(false);
        }
    };

    const handleQuizComplete = async (quizResult) => {
        setPendingResult(quizResult);
        await submitQuizResult(quizResult.rawAnswers || []);
    };

    const handleRetrySubmit = async () => {
        if (!pendingResult || submittingResult) return;
        await submitQuizResult(pendingResult.rawAnswers || []);
    };

    if (loading) return <div className="placeholder-view">Завантаження...</div>;
    if (!quiz) return <div className="placeholder-view">Тест не знайдено</div>;

    if (!isRegistered) {
        return (
            <div className="registration-container">
                <div className="registration-card">
                    <h2>Реєстрація на тест</h2>
                    <p>Квіз: <strong>{quiz.title}</strong></p>
                    {quiz.timeLimit > 0 && <p className="quiz-time-limit">Час на проходження: {quiz.timeLimit} хв</p>}
                    <form onSubmit={handleRegister}>
                        <div className="form-group">
                            <label>Ім'я</label>
                            <input
                                value={studentInfo.firstName}
                                onChange={(event) => setStudentInfo({ ...studentInfo, firstName: event.target.value })}
                                required
                                placeholder="Введіть ім'я"
                            />
                        </div>
                        <div className="form-group">
                            <label>Прізвище</label>
                            <input
                                value={studentInfo.lastName}
                                onChange={(event) => setStudentInfo({ ...studentInfo, lastName: event.target.value })}
                                required
                                placeholder="Введіть прізвище"
                            />
                        </div>
                        <div className="form-group">
                            <label>Місто</label>
                            {quiz.cityBindingEnabled ? (
                                <input
                                    value={studentInfo.city || '—'}
                                    disabled
                                    className="form-control-disabled"
                                />
                            ) : (
                                <select
                                    value={studentInfo.city}
                                    onChange={(event) => setStudentInfo({ ...studentInfo, city: event.target.value })}
                                    required
                                >
                                    <option value="">Оберіть місто...</option>
                                    {availableCities.map(city => (
                                        <option key={city._id} value={city.name}>{city.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <div className="form-group">
                            <label>Посада</label>
                            <input
                                value={studentInfo.position}
                                onChange={(event) => setStudentInfo({ ...studentInfo, position: event.target.value })}
                                required
                                placeholder="Введіть посаду"
                            />
                        </div>
                        <button type="submit" className="btn-save-template btn-start-test">Почати квіз</button>
                    </form>
                </div>
            </div>
        );
    }

    if (submittingResult && pendingResult && !result) {
        return (
            <div className="registration-container">
                <div className="registration-card quiz-progress-card">
                    <h2>Зберігаємо результат...</h2>
                    <p>Ваші відповіді вже перевірені. Зачекайте, поки система збереже результат.</p>
                    <div className="quiz-pending-percent">Поточний результат: <strong>{pendingResult.percentage}%</strong></div>
                </div>
            </div>
        );
    }

    if (submitError && pendingResult && !result) {
        const isPassed = pendingResult.percentage >= (quiz.passingScore || 80);
        return (
            <div className="registration-container">
                <div className="registration-card quiz-error-card">
                    <h2>Результат не збережено</h2>
                    <p className="quiz-error-text">{submitError}</p>
                    <div className={`quiz-local-result ${isPassed ? 'passed' : 'failed'}`}>
                        Локальний результат: {pendingResult.percentage}%
                    </div>
                    <p className="quiz-error-sub">
                        Ми не показуємо успішне завершення, поки сервер не підтвердить збереження.
                    </p>
                    <button type="button" className="btn-save-template btn-start-test" onClick={handleRetrySubmit} disabled={submittingResult}>
                        Спробувати зберегти ще раз
                    </button>
                </div>
            </div>
        );
    }

    if (result) {
        const isPassed = result.percentage >= (quiz.passingScore || 80);
        return (
            <div className="registration-container">
                <div className="registration-card quiz-result-card">
                    <div className="quiz-result-emoji">{isPassed ? '🎉' : '📝'}</div>
                    <h2>Результат: {result.percentage}%</h2>
                    <div className={`quiz-result-status ${isPassed ? 'passed' : 'failed'}`}>
                        {isPassed ? 'ТЕСТ ПРОЙДЕНО' : 'ТЕСТ НЕ ЗДАНО'}
                    </div>
                    <p>Ви відповіли правильно на {result.score} з {result.total} питань.</p>
                    {result.issuedCertificateId && (
                        <div className="quiz-certificate-wrapper">
                            <a
                                href={`/verify-certificate/${result.issuedCertificateId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-certificate-download"
                            >
                                🎓 Отримати сертифікат (PDF)
                            </a>
                        </div>
                    )}
                    <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            className="btn-save-template btn-start-test"
                            onClick={() => {
                                window.location.href = '/dashboard';
                            }}
                        >
                            ✓ Повернутися до навчання
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ background: '#0a0a0a', minHeight: '100vh', color: '#fff', padding: '2rem' }}>
            <QuizEngine
                key={`${hash}-${quiz.attemptProgress || 0}`}
                questions={quiz.questions}
                checkAnswer={checkAnswer}
                onComplete={handleQuizComplete}
                passingScore={quiz.passingScore || 80}
                timeLimit={quiz.timeLimit || 0}
                title={quiz.title}
                initialQuestionIndex={quiz.attemptProgress || 0}
                initialAnswers={quiz.attemptAnswers || []}
            />
        </div>
    );
};

export default QuizPlay;
